import { IndividualPlanStore } from './individual-plan.store';
import { fb, translate } from '../../../../modules/shared/shared.global-service';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  signal,
  untracked,
  viewChild,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { MenuItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { ButtonComponent } from '../../../kit/button/button.component';
import {
    InputFulltextComponent,
    IOption,
} from '../../../../modules/shared/components/inputs/input-fulltext/input-fulltext.component';
import { Listbox, ListboxChangeEvent } from '@openng/optimus-ui/listbox';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { TranslatePipe } from '@ngx-translate/core';
import { UserChipComponent } from '../../../kit/user-chip/user-chip.component';
import { FolderIndividualPlanObject } from '@libs/types/src/services/object-type/folder/folder-individual-plan.object';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { changeSignal, formAddText } from '../../../../utils/form.utils';
import {
    createPlanFrag,
    getIndividualPlansFrag,
    updatePlanFrag,
} from './individual-plan.query';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import {
    checkOk,
    successDeleted,
} from '../../../../modules/content/content.error';
import { TagsInputComponent } from '../../../kit/input/tags-input/tags-input.component';
import { FileUploadBoxComponent } from '../../../kit/file-upload-box/file-upload-box.component';
import { FolderIndividualPlanUpdateInput } from '@libs/types/src/services/input-type/folder/folder-individual-plan.update-input';
import { FolderIndividualPlanCreateInput } from '@libs/types/src/services/input-type/folder/folder-individual-plan.create-input';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import { SwitchInputComponent } from '../../../kit/input/switch-input/switch-input.component';
import { TableComponent } from '../../../../modules/shared/components/kit/table/table.component';
import {
    individualPlanTargetStatusColor,
    IndividualPlanTargetStatusEnum,
} from '@libs/types/src/services/enum/folder/individual-plan-target-status.enum';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { IndividualPlanTargetDialogComponent } from './individual-plan-target-dialog/individual-plan-target-dialog.component';
import { FolderIndividualPlanTargetUpsertInput } from '@libs/types/src/services/input-type/folder/folder-individual-plan-target.upsert.input';
import { TableColumn, TableColumnType } from '../../../../modules/shared/components/kit/table/table.types';
import { TagComponent } from '../../../kit/tag/tag.component';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import {
    dateify,
    dateifyOrNow,
} from '../../../../modules/shared/helpers/date.helper';
import { IndividualPlanPrintDialogComponent } from './individual-plan-print-dialog/individual-plan-print-dialog.component';
import { FolderIndividualPlanPrintInput } from '@libs/types/src/services/input-type/folder/folder-individual-plan.print-input';
import { SocialServiceOperationEnum } from '@libs/types/src/services/enum/social-service-operation.enum';
import { TargetData } from './individual-plan.types';
import { v4 as newUuid } from 'uuid';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { SelectUserComponent } from '../../../../modules/content/components/common/select-user/select-user.component';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { wysiwygPatterGroupsFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import config from '../../../../config/config';
import { SubscriptionComponent } from '../../../../modules/shared/helpers/subscription.component';
import { FolderGlobalStore } from '../../../../store/medicalService/folder.global-store';
import { SocialServiceOffersSelectInputComponent } from '../../social-service-offers-select-input/social-service-offers-select-input.component';
import { BackgroundTaskLaunchService } from '../../../../modules/layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';

@Component({
    selector: 'app-individual-plan',
    templateUrl: './individual-plan.component.html',
    styleUrl: './individual-plan.component.scss',
    providers: [IndividualPlanStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        ContextMenu,
        InputFulltextComponent,
        Listbox,
        ServicePatternWysiwygComponent,
        TranslatePipe,
        UserChipComponent,
        FormsModule,
        TagsInputComponent,
        FileUploadBoxComponent,
        SwitchInputComponent,
        TableComponent,
        TagComponent,
        ReactiveFormsModule,
        DateInputComponent,
        SelectUserComponent,
        SocialServiceOffersSelectInputComponent,
        PrimeTemplate,
    ],
})
export class IndividualPlanComponent extends SubscriptionComponent {
    public readonly store = inject(IndividualPlanStore);

    protected readonly dialogService = inject(DialogService);
    protected readonly folderStore = inject(FolderGlobalStore);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );

    protected readonly filesUpload = viewChild(FileUploadBoxComponent);
    protected readonly offersInput = viewChild.required(
        SocialServiceOffersSelectInputComponent,
    );

    protected form = fb.group({
        search: fb.control<string>(''),
        date: fb.control<Date>(new Date(), Validators.required),
        situation: fb.control<string>('', { nonNullable: true }),
        process: fb.control<string>('', { nonNullable: true }),
        evaluation: fb.control<string>('', { nonNullable: true }),
        tags: fb.control<string[]>([]),
        files: fb.control<number[]>([]),
        showAllTargets: fb.control<boolean>(false),
        selectOffer: fb.control<number | null>(null),
        revisionDate: fb.control<Date | null>(null),
        revisionUserId: fb.control<string | null>(null),
    });

    protected selectedItem: FolderIndividualPlanObject | null = null;
    protected searchSubject = new Subject<string | null>();

    protected targetColumns: TableColumn[] = [
        {
            name: 'dateFrom',
            type: TableColumnType.Date,
            filterable: false,
        },
        { name: 'name' },
        { name: 'status' },
        {
            name: 'dateTo',
            type: TableColumnType.Date,
            filterable: false,
        },
    ];

    protected readonly showAllTargetChange = changeSignal(
        this.form.controls.showAllTargets,
    );
    protected readonly targets = signal<TargetData[]>([]);

    protected readonly visibleTargets = computed(() => {
        const targets = this.targets();
        if (this.showAllTargetChange()) {
            return targets;
        } else {
            return targets.filter(
                (it) =>
                    it.status === IndividualPlanTargetStatusEnum.IN_PROGRESS,
            );
        }
    });
    protected readonly inactiveTargets = computed<boolean>(() =>
        this.targets().some(
            (it) => it.status !== IndividualPlanTargetStatusEnum.IN_PROGRESS,
        ),
    );

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: translate.instant('individualPlan.message.update'),
            icon: 'pi pi-file-edit',
            command: () => this.save(true),
        },
        {
            label: translate.instant('individualPlan.message.delete'),
            icon: 'pi pi-times',
            command: () => this.delete(),
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    protected contextItem: number = 0;

    constructor() {
        super();

        effect(() => {
            this.fetchData();
        });

        effect(() => {
            this.subs.push(
                this.searchSubject
                    .pipe(debounceTime(config.debounceTime))
                    .subscribe((it) => this.fetchData(it)),
            );
        });

        effect(() => {
            const plans = this.store.history();
            const currentId = this.selectedItem?.id;

            const selectedPlan = plans.find((it) => it.id == currentId);
            if (selectedPlan) {
                this.selectedItem = selectedPlan;
                this.patchForm();
                return;
            }

            if (plans.length > 0) {
                this.selectedItem = plans[0];
                this.patchForm();
            }
        });

        effect(() => {
            if (
                !this.store.isAllowed('folderRecord.create') ||
                this.folderStore.isTerminated()
            ) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        });

        this.store.onSuccess(
            () => this.fetchData(),
            'createIndividualPlan',
            'updateIndividualPlan',
            'deleteIndividualPlan',
        );
    }

    @HostListener('document:contextmenu')
    public rightClickAny() {
        this.ctx.hide();
    }

    @HostListener('document:click')
    public leftClickAny() {
        this.ctx.hide();
    }

    public rightClick(
        menu: ContextMenu,
        event: MouseEvent,
        item: FolderIndividualPlanObject,
    ) {
        this.contextItem = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(
            this.selectedItem?.id === item.id ? 0 : 1,
        );
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    protected async delete(): Promise<void> {
        if (!this.contextItem) return;

        await this.store.api.deleteIndividualPlan({ id: this.contextItem });
        successDeleted();
    }

    protected async save(update: boolean = false): Promise<void> {
        if (isInvalid(this.form)) return;
        if (!this.store.folderId()) return;
        if (update && !this.contextItem) return;

        const newFiles = await this.uploadFiles();

        const _data = {
            date: this.form.value.date?.toISOString(),
            situation: this.form.value.situation || '',
            process: this.form.value.process || '',
            evaluation: this.form.value.evaluation || '',
            revisionDate: this.form.value.revisionDate || null,
            revisionUserId: this.form.value.revisionUserId || null,
            tags: this.form.value.tags || [],
            files: [...(this.form.value.files || []), ...newFiles],
            targets: this.targets().map(
                (it) =>
                    <FolderIndividualPlanTargetUpsertInput>{
                        ...it,
                        uuid: undefined,
                        __typename: undefined,
                    },
            ),
            offers: this.offersInput().getOffers(),
        };

        if (update) {
            const data = _data as FolderIndividualPlanUpdateInput;
            data.id = this.contextItem;
            const res = await this.store.api.updateIndividualPlan(
                data,
                updatePlanFrag,
            );
            checkOk(res, 'update');
        } else {
            const data = _data as FolderIndividualPlanCreateInput;
            data.folderId = this.store.folderId();
            if (this.selectedItem?.date == data.date) {
                data.date = new Date().toISOString();
            }

            const res = await this.store.api.createIndividualPlan(
                data,
                createPlanFrag,
            );
            checkOk(res);
            this.selectedItem = null;
        }
        await this.filesUpload()?.confirmFiles(newFiles);
    }

    protected async print(): Promise<void> {
        this.dialogService.open(IndividualPlanPrintDialogComponent, {
            focusOnShow: false,
            data: {
                serviceId: this.folderStore.folder()?.serviceId || 0,
                folderId: this.store.folderId(),
                onStart: (value: FolderIndividualPlanPrintInput) =>
                    this.backgroundTaskLauncher.start({
                        type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                        payload: {
                            folderId: this.store.folderId(),
                            kind: ClientDocumentExportKind.INDIVIDUAL_PLAN,
                            input: {
                                ...value,
                                folderId: this.store.folderId(),
                            },
                        },
                        alreadyRunningMessage:
                            'person.documentExport.backgroundTask.messages.alreadyRunning',
                        failedMessage:
                            'person.documentExport.backgroundTask.messages.startFailed',
                    }),
            },
        });
    }

    protected listChange(index: ListboxChangeEvent): void {
        if (index.value) {
            this.selectedItem = this.store.history()[index.value.index];
            this.patchForm();
        }
    }

    protected selectedChange(selectedSearch: IOption | null): void {
        this.selectedItem = selectedSearch as any as FolderIndividualPlanObject;
        this.patchForm();
    }

    protected async upsertTarget(target?: TargetData): Promise<void> {
        this.dialogService
            .open(IndividualPlanTargetDialogComponent, {
                header: translate.instant('individualPlan.fields.target'),
                data: { target },
                focusOnShow: false,
                styleClass: 'p-dialog-width-40',
            })
            ?.onClose.subscribe(
                (newTarget: FolderIndividualPlanTargetUpsertInput | null) => {
                    if (!newTarget) return;

                    let list = this.targets();
                    if (target?.uuid) {
                        const oldTarget = list.find(
                            (it) => it.uuid === target.uuid,
                        )!;
                        oldTarget.name = newTarget.name;
                        oldTarget.status = newTarget.status;
                        oldTarget.dateFrom = newTarget.dateFrom;
                        oldTarget.dateTo = newTarget.dateTo;
                    } else {
                        list.unshift({
                            ...newTarget,
                            uuid: newUuid(),
                        });
                    }
                    this.targets.set([...list.map((it) => ({ ...it }))]);
                },
            );
    }

    protected deleteTarget(target: any): void {
        this.targets.update((arr) =>
            arr.filter((it: any) => it.uuid !== target.uuid),
        );
    }

    private patchForm(): void {
        // No idea why untracted is required, just keep it there as Angular and whole Reactive programming concept is A FUCKING BULLSHIT
        untracked(() => {
            if (this.selectedItem) {
                this.form.patchValue({
                    date: dateifyOrNow(this.selectedItem?.date),
                    situation: this.selectedItem?.situation ?? '',
                    process: this.selectedItem?.process ?? '',
                    evaluation: this.selectedItem?.evaluation ?? '',
                    tags: this.selectedItem?.tags || [],
                    files: this.selectedItem?.files || [],
                    revisionDate: dateify(this.selectedItem?.revisionDate),
                    revisionUserId: this.selectedItem?.revisionUserId || null,
                });
                this.targets.set(
                    (this.selectedItem?.targets || []).map(
                        (it) =>
                            <TargetData>{
                                ...it,
                                uuid: newUuid(),
                            },
                    ),
                );
                this.offersInput().patchOffers(this.selectedItem?.offers);
            } else {
                this.form.reset();
            }
        });
    }

    private fetchData(search?: string | null): void {
        this.store.api.getIndividualPlans(
            FilterBuilder.singlePage({
                folderId: this.folderStore.folderId(),
                sort: { column: 'createdAt', direction: 'DESC' },
                search,
            }),
            getIndividualPlansFrag.replaceAll(
                'VALID_AT',
                new Date().toISOString(),
            ),
        );
        this.store.api.getIndividualPlanServiceTagsByFolder({
            id: this.folderStore.folderId(),
        });
    }

    private async uploadFiles(): Promise<number[]> {
        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.INDIVIDUAL_PLAN,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: [
                {
                    externalType: ExternalType.folder,
                    externalId: String(this.store.folderId()),
                },
                {
                    externalType: ExternalType.service,
                    externalId: String(
                        this.folderStore.folder()?.serviceId || 0,
                    ),
                },
            ],
        };

        return this.filesUpload()?.uploadFiles(metadata) || [];
    }

    protected readonly ServicePatternStructureUsageEnum =
        ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;

    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly wysiwygPatterGroupsFolder = wysiwygPatterGroupsFolder;
    protected readonly SocialServiceOperationEnum = SocialServiceOperationEnum;
    protected readonly individualPlanTargetStatusColor =
        individualPlanTargetStatusColor;
}
